
import React, { useEffect, useState } from "react";
import {
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Button,
  Input,
  Label,
  Spinner,
} from "reactstrap";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Request from "../../../../helpers/request";
import * as url from "../../../../helpers/url_helper";
import DeviceControl from "./Common/DeviceControl";
type CategoryKey = "switch" | "aircurtain" | "airdriver" | "irblaster";

const CATEGORY_LABELS: Record<CategoryKey, string> = {
  switch: "Switch",
  aircurtain: "Air Curtain",
  airdriver: "Air Driver",
  irblaster: "IR Blaster",
};


const CATEGORY_ENDPOINTS: Record<CategoryKey, string> = {
  switch: url.GET_CHIPS_LIST,
  aircurtain: url.GET_CURTAIN_LIST,
  airdriver: url.GET_GATEWAY_LIST,
  irblaster: url.GET_BLASTER_LIST,
};

// Air driver brightness/temperature bounds
const AIR_DRIVER_DEFAULT_BRIGHTNESS = 100;
const AIR_DRIVER_DEFAULT_TEMP = 4000;

export const ScenePanel = [
  "Air_3SC",
  "3SC",
  "4SC",
  "Air_4SC",
  "4HISC",
  "Air_4HISC",
  "4HINSC",
  "Air_4HINSC",
  "2ISC"
]

interface RawSwitch {
  _id: string;
  button?: string;
  displayName?: string;
  type?: number;
  isOn?: boolean;
  isDimmable?: boolean;
  dimmerValue?: string;
}

interface RawDriver {
  _id: string;
  id?: string;
  name?: string;
  isOn?: boolean;
  isOnline?: boolean;
  brightness?: number; // 0-100
  temp?: number; // color temperature in Kelvin, e.g. 2700-6500
  isDimmable?: boolean;
}

interface RawBlasterRemote {
  id: string;
  name?: string;
  displayName?: string;
  type?: string;
  roomId?: { id?: string; name?: string };
  brandName?: string;
  brandModal?: string;
}

interface DeviceRow {
  id: string;
  name: string;
  roomName?: string;
  isOnline?: boolean;
  switches?: RawSwitch[]; // present for chip/switch rows
  drivers?: RawDriver[]; // present for air-driver gateway rows
  remotes?: RawBlasterRemote[];
  raw: any;
  modelType: string;
  type: string;
}

interface SelectedItem {
  isOn: boolean;
  isDimmable: boolean;
  dimmerValue: string;
  executeValue?: string;
  curtainControl?: "quick" | "precise";
  button?: string;
  brightness?: number; // air driver only, 1-100
  temperature?: number; // air driver only, 2700-6500
  blasterMode?: "cool" | "auto" | "fan only" | "heat";
  blasterFan?: "1" | "2" | "3" | "4" | "5" ;
  blasterSwing?: "auto" | "h-swing" | "v-swing";
  category: CategoryKey;
}

interface SelectedSwitchState {
  [itemId: string]: SelectedItem;
}

interface CreateSceneOffcanvasProps {
  isOpen: boolean;
  toggle: () => void;
  userId: any;
  selectedPremise?: any;
  onSceneCreated: () => void;
  editingScene?: any;
}

const handleUnauthorized = () => {
  localStorage.removeItem("userInfo");
  localStorage.removeItem("selectedPremiseID");
  localStorage.removeItem("selectedUserID");
  localStorage.removeItem("selectedUserEmail");
  localStorage.removeItem("Authorization");
  localStorage.removeItem("permissions");
  window.location.reload();
};

// Normalizes a raw row from any of the 4 endpoints into a DeviceRow.
const normalizeDevice = (row: any): DeviceRow => ({
  id: row.id || row._id || row.slave,
  name: row.name || row.displayName || row.slave,
  roomName: row.roomName,
  isOnline: row.isOnline,
  switches: row.switches,
  drivers: row.drivers,
  remotes: row.remotes,
  raw: row,
  modelType: row.modelType,
  type: row.type,
});

// Curtain executeValue is always: button + dimmerValue + "1"
const buildCurtainExecuteValue = (button: string, dimmerValue: string) => {
  return `${button}${dimmerValue}1`;
};

// Builds the (category-room-modelType) group id used both for rendering
// and for the auto-expand logic below. Keeping this in one place means
// the grouping key can never drift between the two.
const buildGroupId = (
  category: CategoryKey,
  roomName: string,
  modelType: string,
) => `${category}-${roomName}-${modelType}`;

const groupDevicesByRoomAndModel = (devices: DeviceRow[]) => {
  return devices.reduce((rooms, device) => {
    const roomName =
      device.roomName?.trim() || device?.name?.trim();

    const modelType =
      device.modelType?.trim() || device?.type?.trim() ;

    const uniqueKey = `${modelType}_${device.id}`;

    if (!rooms[roomName]) {
      rooms[roomName] = {};
    }

    if (!rooms[roomName][uniqueKey]) {
      rooms[roomName][uniqueKey] = [];
    }

    rooms[roomName][uniqueKey].push(device);

    return rooms;
  }, {} as Record<string, Record<string, DeviceRow[]>>);
};


const CreateSceneOffcanvas = ({
  isOpen,
  toggle,
  userId,
  selectedPremise,
  onSceneCreated,
  editingScene,
}: CreateSceneOffcanvasProps) => {
  // selectedPremise may be the id itself, or an object with .id (as used in DeviceBinding.tsx)
  const premiseId = selectedPremise?.id ?? selectedPremise;
  const isEditMode = !!editingScene;

  const selectedRooms = useSelector(
    (state: any) => state.APIResponse.selectedRooms,
  );

  // step 1 = scene name, step 2 = pick a device category, step 3 = pick devices/switches
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [sceneName, setSceneName] = useState("");
  const [activeCategory, setActiveCategory] = useState<CategoryKey | null>(
    null,
  );
  // devices grouped by category so edit mode can show everything at once
  const [devicesByCategory, setDevicesByCategory] = useState<
    Partial<Record<CategoryKey, DeviceRow[]>>
  >({});
  const [expandedDeviceIds, setExpandedDeviceIds] = useState<Set<string>>(
    new Set(),
  );
  const [selectedSwitches, setSelectedSwitches] = useState<SelectedSwitchState>(
    {},
  );
  // Master switch: true = "All On", false = "All Off"
  const [allOn, setAllOn] = useState<boolean>(true);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [autoExpandedForScene, setAutoExpandedForScene] = useState<
    string | null
  >(null);
 
  const [pendingEditRaw, setPendingEditRaw] = useState<{
    switches: any[];
    airDriverInfo: any[];
    remotes: any[];
  } | null>(null);

  useEffect(() => {
    if (!isOpen) {
      resetForm();
      return;
    }

    if (editingScene) {
      setStep(1);
      setSceneName(editingScene.name || "");

      setPendingEditRaw({
        switches: editingScene.switches || [],
        airDriverInfo: editingScene.airDriverInfo || [],
        remotes: editingScene.remotes || [],
      });

      const sceneKey = editingScene._id || editingScene.id || editingScene.name;
      setAutoExpandedForScene((prev) => (prev === sceneKey ? prev : null));
      fetchAllCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, editingScene]);

  useEffect(() => {
    if (!isEditMode) return;
    if (!pendingEditRaw) return;
    if (Object.keys(devicesByCategory).length === 0) return;

    const switchDevices = devicesByCategory.switch || [];
    const curtainDevices = devicesByCategory.aircurtain || [];
    const driverDevices = devicesByCategory.airdriver || [];
    const blasterDevices = devicesByCategory.irblaster || [];

    const findChipSwitchId = (sw: any): string | null => {
      const directId = sw.chipSwitchId || sw._id || sw.id;
      if (directId) return directId;

      if (sw.slave != null && sw.button != null) {
        for (const device of switchDevices) {
          const deviceSlave = device.raw?.slave ?? device.id;
          if (deviceSlave === sw.slave) {
            const match = (device.switches || []).find(
              (candidate) => candidate.button === sw.button,
            );
            if (match) return match._id;
          }
        }
      }
      return null;
    };

    const findCurtainId = (sw: any): string | null => {
      const directId = sw.airCurtainId;
      if (directId) return directId;

      if (sw.slave != null) {
        const match = curtainDevices.find(
          (device) => device.raw?.slave === sw.slave || device.id === sw.slave,
        );
        if (match) return match.id;
      }
      return null;
    };

    const findAirDriverId = (driver: any): string | null => {
      const directId = driver.airDriverId || driver.id || driver._id;
      if (directId) return directId;

      if (driver.slave != null) {
        for (const device of driverDevices) {
          const deviceSlave = device.raw?.slave ?? device.id;
          if (deviceSlave === driver.slave) {
            const match = (device.drivers || [])[0];
            if (match) return match._id || match.id || null;
          }
        }
      }
      return null;
    };

    const findRemoteId = (remote: any): string | null => {
      // Prefer matching by the panel's slave first — a saved remote's
      // `_id`/`blasterRemoteId` field is often just the scene's own
      // subdocument id, not the actual remote id from the device list.
      if (remote.slave != null) {
        for (const device of blasterDevices) {
          const deviceSlave = device.raw?.slave ?? device.id;
          if (deviceSlave === remote.slave) {
            const match = (device.remotes || [])[0];
            if (match) return match.id || null;
          }
        }
      }

      const directId = remote.blasterRemoteId || remote._id || remote.id;
      if (directId) return directId;

      return null;
    };

    const selected: SelectedSwitchState = {};
    const unmatched: any[] = [];

    pendingEditRaw.switches.forEach((sw: any) => {
      // Explicit air-curtain shape (chipSwitchId-free, has executeValue we
      // can decode directly) takes priority when airCurtainId is present.
      if (sw.airCurtainId) {
        const itemId = sw.airCurtainId;
        const executeValue: string = sw.executeValue || "";
        const button = executeValue.slice(0, 2) || "01";
        const dimmerValue = executeValue.slice(2, 4) || "00";
        const isOn = button === "01" ? dimmerValue === "00" : true;

        selected[itemId] = {
          isOn,
          isDimmable: true,
          dimmerValue,
          executeValue,
          button,
          curtainControl: button === "02" ? "precise" : "quick",
          category: "aircurtain",
        };
        return;
      }

      const chipSwitchId = findChipSwitchId(sw);
      if (chipSwitchId) {
        selected[chipSwitchId] = {
          isOn: sw.isOn ?? true,
          isDimmable: sw.isDimmable ?? false,
          dimmerValue: sw.dimmerValue || "XX",
          category: "switch",
        };
        return;
      }

      const curtainId = findCurtainId(sw);
      if (curtainId) {
        const executeValue: string = sw.executeValue || "";
        const button = executeValue.slice(0, 2) || "01";
        const dimmerValue = executeValue.slice(2, 4) || sw.dimmerValue || "00";
        const isOn = button === "01" ? dimmerValue === "00" : true;

        selected[curtainId] = {
          isOn,
          isDimmable: true,
          dimmerValue,
          executeValue: executeValue || buildCurtainExecuteValue(button, dimmerValue),
          button,
          curtainControl: button === "02" ? "precise" : "quick",
          category: "aircurtain",
        };
        return;
      }

      if (sw.slave != null && sw.button != null) {
        const nodeId = Number(sw.button);
        let driverMatchId: string | null = null;

        for (const device of driverDevices) {
          const found = (device.drivers || []).find(
            (driver: any) =>
              driver.slave === sw.slave && Number(driver.nodeId) === nodeId,
          );
          if (found) {
            driverMatchId = found._id || found.id || null;
            break;
          }
        }

        if (driverMatchId) {
          const parts = (sw.executeValue || "").split("X");
          const temperature = parts[0]
            ? Number(parts[0])
            : AIR_DRIVER_DEFAULT_TEMP;
          const brightness = sw.dimmerValue
            ? Number(sw.dimmerValue)
            : parts[1]
            ? Number(parts[1])
            : AIR_DRIVER_DEFAULT_BRIGHTNESS;

          selected[driverMatchId] = {
            isOn: sw.isOn ?? false,
            isDimmable: true,
            dimmerValue: "",
            brightness,
            temperature,
            category: "airdriver",
          };
          return;
        }

        const blasterDevice = blasterDevices.find(
          (device) => device.raw?.slave === sw.slave || device.id === sw.slave,
        );
        const blasterRemote = blasterDevice?.remotes?.[0];
        if (blasterRemote) {
          selected[blasterRemote.id] = {
            isOn: sw.isOn ?? true,
            isDimmable: false,
            dimmerValue: "XX",
            temperature: 24,
            blasterMode: "cool",
            blasterFan: "1",
            blasterSwing: "auto",
            category: "irblaster",
          };
          return;
        }
      }

      unmatched.push({ source: "switches/curtain", raw: sw });
    });

    pendingEditRaw.airDriverInfo.forEach((driver: any) => {
      const itemId = findAirDriverId(driver);
      if (!itemId) {
        unmatched.push({ source: "airDriverInfo", raw: driver });
        return;
      }

      selected[itemId] = {
        isOn: driver.status === 1 || driver.isOn === true,
        isDimmable: true,
        dimmerValue: "",
        brightness: driver.brightness ?? AIR_DRIVER_DEFAULT_BRIGHTNESS,
        temperature: driver.temperature ?? AIR_DRIVER_DEFAULT_TEMP,
        category: "airdriver",
      };
    });

    pendingEditRaw.remotes.forEach((remote: any) => {
      const itemId = findRemoteId(remote);
      if (!itemId) {
        unmatched.push({ source: "remotes", raw: remote });
        return;
      }

      const execData = remote.blasterRemoteExecuteData || {};

      selected[itemId] = {
        isOn: remote.isOn ?? true,
        isDimmable: false,
        dimmerValue: "XX",
        temperature: execData.temperature ?? 24,
        blasterMode: execData.mode ?? "cool",
        blasterFan: execData.fan ?? "1",
        blasterSwing: execData.swing ?? "auto",
        category: "irblaster",
      };
    });

    if (unmatched.length > 0) {
      console.warn(
        "CreateSceneOffcanvas: could not match these saved scene items to any fetched device — they will not show as selected in edit mode:",
        unmatched,
      );
    }

    setSelectedSwitches(selected);

    const hasOffSwitch = Object.values(selected).some(
      (item) => item.isOn === false,
    );
    setAllOn(!hasOffSwitch);

    setPendingEditRaw(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, pendingEditRaw, devicesByCategory]);

  // Auto-expand every (category, room, modelType) group that already has a
  // selected item, so opening "Edit" shows the scene's current devices
  // expanded and visible immediately instead of everything collapsed.
  useEffect(() => {
    if (!isEditMode) return;
    if (Object.keys(devicesByCategory).length === 0) return;
    if (Object.keys(selectedSwitches).length === 0) return;

    const sceneKey = editingScene?._id || editingScene?.id || editingScene?.name;
    if (autoExpandedForScene === sceneKey) return; // already done for this scene

    const newExpanded = new Set<string>();

    (Object.keys(devicesByCategory) as CategoryKey[]).forEach((category) => {
      const devices = devicesByCategory[category] || [];
      const groupedDevices = groupDevicesByRoomAndModel(devices);
      Object.entries(groupedDevices).forEach(([roomName, models]) => {
        Object.entries(models).forEach(([modelType, modelDevices]) => {
          const groupId = buildGroupId(category, roomName, modelType);

          const hasSelected =
            category === "aircurtain"
              ? modelDevices.some((device) => !!selectedSwitches[device.id])
              : category === "airdriver"
              ? modelDevices.some((device) =>
                  (device.drivers || []).some(
                    (driver) => !!selectedSwitches[driver._id || driver.id!],
                  ),
                )
              : category === "irblaster"
              ? modelDevices.some((device) =>
                  (device.remotes || []).some(
                    (remote) => !!selectedSwitches[remote.id],
                  ),
                )
              : modelDevices.some((device) =>
                  (device.switches || []).some(
                    (sw) => !!selectedSwitches[sw._id],
                  ),
                );

          if (hasSelected) {
            newExpanded.add(groupId);
          }
        });
      });
    });

    if (newExpanded.size > 0) {
      setExpandedDeviceIds((prev) => {
        const merged = new Set(prev);
        newExpanded.forEach((id) => merged.add(id));
        return merged;
      });
    }

    setAutoExpandedForScene(sceneKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, devicesByCategory, selectedSwitches]);

  const resetForm = () => {
    setStep(1);
    setSceneName("");
    setActiveCategory(null);
    setDevicesByCategory({});
    setExpandedDeviceIds(new Set());
    setSelectedSwitches({});
    setAllOn(true);
    setAutoExpandedForScene(null);
    setPendingEditRaw(null);
  };

  const goToNextFromName = () => {
    if (!sceneName.trim()) {
      toast.error("Please enter a scene name");
      return;
    }

    setStep(3);

    // Create mode: load all device categories
    if (!isEditMode && Object.keys(devicesByCategory).length === 0) {
      fetchAllCategories();
    }

    // Edit mode already loads all categories when opened
    if (isEditMode && Object.keys(devicesByCategory).length === 0) {
      fetchAllCategories();
    }
  };

  const buildRoomId = () => {
    if (!selectedRooms || selectedRooms.length === 0) return "noroom";
    const ids = selectedRooms.map((r: any) =>
      typeof r === "string" ? r : r.id,
    );
    return [...ids].join(",");
  };

  const buildListPayload = () => ({
    draw: 1,
    order: [],
    start: 0,
    length: 1000,
    search: { value: "", regex: false },
    userId,
    premiseId,
    roomId: buildRoomId(),
  });

  const fetchCategory = async (category: CategoryKey) => {
    setLoadingDevices(true);
    try {
      const response = await Request.post(
        `${url.BASE_URL}${CATEGORY_ENDPOINTS[category]}`,
        buildListPayload(),
      );
      const rows = response.data?.data || [];
      setDevicesByCategory((prev) => ({
        ...prev,
        [category]: rows.map(normalizeDevice),
      }));
    } catch (error: any) {
      console.error(`Failed to fetch ${category} list`, error);
      toast.error(`Failed to load ${CATEGORY_LABELS[category]} devices`);
      setDevicesByCategory((prev) => ({ ...prev, [category]: [] }));
      if (error.response?.status === 401) {
        handleUnauthorized();
      }
    } finally {
      setLoadingDevices(false);
    }
  };

  const fetchAllCategories = async () => {
    setLoadingDevices(true);
    try {
      const categories = Object.keys(CATEGORY_ENDPOINTS) as CategoryKey[];
      const results = await Promise.all(
        categories.map((category) =>
          Request.post(
            `${url.BASE_URL}${CATEGORY_ENDPOINTS[category]}`,
            buildListPayload(),
          )
            .then((res) => ({
              category,
              rows: (res.data?.data || []).map(normalizeDevice),
            }))
            .catch((error) => {
              console.error(`Failed to fetch ${category} list`, error);
              if (error.response?.status === 401) handleUnauthorized();
              return { category, rows: [] as DeviceRow[] };
            }),
        ),
      );
      // console.log("fetch all deive ce data",results)

      const grouped: Partial<Record<CategoryKey, DeviceRow[]>> = {};
      results.forEach(({ category, rows }) => {
        grouped[category] = rows;
      });
      setDevicesByCategory(grouped);
    } finally {
      setLoadingDevices(false);
    }
  };

  const toggleExpandDevice = (deviceId: string) => {
    setExpandedDeviceIds((prev) => {
      const next = new Set(prev);
      if (next.has(deviceId)) next.delete(deviceId);
      else next.add(deviceId);
      return next;
    });
  };

  const isChecked = (itemId: string) => !!selectedSwitches[itemId];

  // Select/unselect a sub-switch inside a chip (category === "switch")
  const toggleSwitchSelection = (
    sw: RawSwitch,
    category: CategoryKey = "switch",
  ) => {
    setSelectedSwitches((prev) => {
      const updated = { ...prev };

      if (updated[sw._id]) {
        delete updated[sw._id];
      } else {
        updated[sw._id] = {
          isOn: allOn,
          isDimmable: category === "aircurtain" || sw.type === 2,
          dimmerValue:
            category === "aircurtain"
              ? "00" // quick control, open
              : sw.type === 0
              ? "XX"
              : "1",
          executeValue: category === "aircurtain" ? "01001" : undefined,
          curtainControl: category === "aircurtain" ? "quick" : undefined,
          button: category === "aircurtain" ? "01" : undefined,
          category,
        };
      }

      return updated;
    });
  };

  // Select/unselect a single light inside an air-driver gateway
  const toggleDriverSelection = (driver: RawDriver) => {
    const itemId = driver._id || driver.id!;

    setSelectedSwitches((prev) => {
      const updated = { ...prev };

      if (updated[itemId]) {
        delete updated[itemId];
      } else {
        updated[itemId] = {
          isOn: driver.isOn ?? allOn,
          isDimmable: true,
          dimmerValue: "",
          brightness: driver.brightness ?? AIR_DRIVER_DEFAULT_BRIGHTNESS,
          temperature: driver.temp ?? AIR_DRIVER_DEFAULT_TEMP,
          category: "airdriver",
        };
      }

      return updated;
    });
  };

  const toggleIndividualOnOff = (itemId: string) => {
    setSelectedSwitches((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        isOn: !prev[itemId].isOn,
      },
    }));
  };

  //common
  const updateSelectedItem = (
  itemId: string,
  updates: Partial<SelectedItem>,
) => {
  setSelectedSwitches((prev) => ({
    ...prev,
    [itemId]: {
      ...prev[itemId],
      ...updates,
    },
  }));
};

  // Master toggle: sets every currently-selected item on/off at once
  const handleAllOnOffToggle = () => {
    const newAllOn = !allOn;
    setAllOn(newAllOn);
    setSelectedSwitches((prev) => {
      const updated: SelectedSwitchState = {};
      Object.keys(prev).forEach((itemId) => {
        updated[itemId] = { ...prev[itemId], isOn: newAllOn };
      });
      return updated;
    });
  };

  const handleCreateScene = async () => {
    if (!sceneName.trim()) {
      toast.error("Please enter a scene name");
      return;
    }

    const entries = Object.entries(selectedSwitches);

    if (entries.length === 0) {
      toast.error("Please select at least one device/switch");
      return;
    }
    // NORMAL SWITCHES
    const normalSwitchesPayload = entries
      .filter(([, val]) => val.category === "switch")
      .map(([chipSwitchId, val]) => {
        const deviceSwitch = devicesByCategory.switch
          ?.flatMap((device) => device.switches || [])
          .find((sw) => sw._id === chipSwitchId);
        const switchType = deviceSwitch?.type ?? 0;
        return {
          chipSwitchId,
          isOn: val.isOn,
          isDimmable: switchType === 2,
          dimmerValue: switchType === 0 ? "XX" : val.dimmerValue ?? "1",
        };
      });

    // AIR CURTAINS
    const curtainSwitchesPayload = entries
      .filter(([, val]) => val.category === "aircurtain")
      .map(([curtainId, val]) => {
        const curtainDevice = devicesByCategory.aircurtain?.find(
          (device) => device.id === curtainId,
        );

        if (!curtainDevice) {
          console.error(
            "Curtain device not found:",
            curtainId,
            devicesByCategory.aircurtain,
          );

          return null;
        }

        const airCurtainId =
          curtainDevice.raw?._id || curtainDevice.id || curtainId;

        // "01" = quick control (open/close), "02" = precise control (position)
        const button =
          val.button || (val.curtainControl === "precise" ? "02" : "01");

        // Make sure curtain always has a valid hex position.
        const dimmerValue = val.dimmerValue || "00";

        // executeValue is always: button + dimmerValue + "1"
        const executeValue =
          val.executeValue || buildCurtainExecuteValue(button, dimmerValue);

        // Backend only accepts these two fields for a curtain switch entry.
        const curtainPayload = {
          airCurtainId,
          executeValue,
        };

        return curtainPayload;
      })
      .filter(Boolean);

    const airDriverInfoPayload = entries
      .filter(([, val]) => val.category === "airdriver")
      .map(([airDriverId, val]) => ({
        airDriverId,
        brightness: val.brightness ?? AIR_DRIVER_DEFAULT_BRIGHTNESS,
        temperature: val.temperature ?? AIR_DRIVER_DEFAULT_TEMP,
        status: val.isOn ? 1 : 0,
      }));

    const remotesPayload = entries
      .filter(([, val]) => val.category === "irblaster")
      .map(([blasterRemoteId, val]) => ({
        blasterRemoteId,
        isOn: val.isOn,
        blasterRemoteExecuteData: {
          mode: val.blasterMode ?? "cool",
          temperature: val.temperature ?? 24,
          fan: val.blasterFan ?? "1",
          swing: val.blasterSwing ?? "on",
        },
      }));

    setSubmitting(true);

    try {
      const payload = {
        userId,
        premiseId,
        name: sceneName.trim(),
        bgImage: "surface_light",
        switches: normalSwitchesPayload,
        airCurtainInfo: curtainSwitchesPayload,
        airDriverInfo: airDriverInfoPayload,
        remotes: remotesPayload,
      };

      if (editingScene) {
        const sceneId = editingScene._id || editingScene.id;

        await Request.put(`${url.BASE_URL}${url.UPDATE_SCENE}`, {
          ...payload,
          sceneId,
        });

        toast.success("Scene updated successfully");
      } else {
        await Request.post(`${url.BASE_URL}${url.CREATE_ADMIN_SCENE}`, payload);

        toast.success("Scene created successfully");
      }

      onSceneCreated();
      toggle();
    } catch (error: any) {
      console.error(
        editingScene ? "Failed to update scene" : "Failed to create scene",
        error,
      );

      const message =
        error.response?.data?.msg ||
        error.response?.data?.message ||
        (editingScene ? "Failed to update scene" : "Failed to create scene");

      toast.error(message);

      if (error.response?.status === 401) {
        handleUnauthorized();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const toggleBlasterSelection = (remote: RawBlasterRemote) => {
    const itemId = remote.id;

    setSelectedSwitches((prev) => {
      const updated = { ...prev };

      if (updated[itemId]) {
        delete updated[itemId];
      } else {
        updated[itemId] = {
          isOn: allOn,
          isDimmable: false,
          dimmerValue: "XX",
          temperature: 24,
          blasterMode: "cool",
          blasterFan: "1",
          blasterSwing: "auto",
          category: "irblaster",
        };
      }

      return updated;
    });
  };

  const renderDeviceList = (category: CategoryKey, devices: DeviceRow[]) => {
    // Group devices by roomName -> modelType
    const groupedDevices = groupDevicesByRoomAndModel(devices);

    const toggleCurtainSelection = (device: DeviceRow) => {
      const itemId = device.id;

      setSelectedSwitches((prev) => {
        const updated = { ...prev };

        if (updated[itemId]) {
          delete updated[itemId];
        } else {
          // Default to Quick Control - Open
          updated[itemId] = {
            isOn: true,
            isDimmable: true,

            // Quick control open position
            dimmerValue: "00",

            // button(01) + dimmerValue(00) + 1
            executeValue: "01001",

            button: "01",

            curtainControl: "quick",

            category: "aircurtain",
          };
        }

        return updated;
      });
    };
    return (
      <div className="mb-3">
        {devices.length === 0 ? (
          <div className="text-muted small px-1">
            No devices in this category
          </div>
        ) : (
          Object.entries(groupedDevices).map(([roomName, models]) => (
            <div key={roomName} className="mb-3">
              {/* ROOM NAME */}
              <div
                className="fw-semibold px-1 mb-2"
                style={{
                  fontSize: 15,
                  color: "#1e293b",
                }}
              >
                {roomName}
              </div>

              {/* MODEL TYPES */}
              {Object.entries(models).map(([modelType, modelDevices]) => {
                const firstDevice = modelDevices[0];

                const groupId = buildGroupId(category, roomName, modelType);
                // Only for displaying the UI
                const deviceModelType =
                  firstDevice?.modelType?.trim() ||
                  firstDevice?.type?.trim() ||
                  "Unknown Type";

                if (ScenePanel.includes(deviceModelType)) {
                    return null;
                  }

                const isExpanded = expandedDeviceIds.has(groupId);

                // Normal switch data
                const allSwitches =
                  category === "switch" || category === "irblaster"
                    ? modelDevices.flatMap((device) => device.switches || [])
                    : [];

                // Curtain data — curtain itself is the selectable item
                const curtainDevices =
                  category === "aircurtain" ? modelDevices : [];

                // Air driver data — each light inside the gateway is the selectable item
                const allDrivers =
                  category === "airdriver"
                    ? modelDevices.flatMap((device) => device.drivers || [])
                    : [];

                const allBlasterRemotes =
                  category === "irblaster"
                    ? modelDevices.flatMap((device) => device.remotes || [])
                    : [];

                const checkedCount =
                  category === "aircurtain"
                    ? curtainDevices.filter((device) => isChecked(device.id))
                        .length
                    : category === "airdriver"
                    ? allDrivers.filter((driver) =>
                        isChecked(driver._id || driver.id!),
                      ).length
                    : category === "irblaster"
                    ? allBlasterRemotes.filter((remote) =>
                        isChecked(remote.id),
                      ).length
                    : allSwitches.filter((sw) => isChecked(sw._id)).length;

                const hasItems =
                  category === "aircurtain"
                    ? curtainDevices.length > 0
                    : category === "airdriver"
                    ? allDrivers.length > 0
                    : category === "irblaster"
                    ? allBlasterRemotes.length > 0
                    : allSwitches.length > 0;

                return (
                  <div
                    key={groupId}
                    className="border rounded mb-2 overflow-hidden ms-2"
                  >
                    {/* MODEL TYPE HEADER */}
                    <button
                      type="button"
                      className="w-100 border-0 bg-white text-start p-2 d-flex align-items-center gap-2"
                      onClick={() => {
                        if (hasItems) {
                          toggleExpandDevice(groupId);
                        }
                      }}
                    >
                      {hasItems ? (
                        <i
                          className={`bx ${
                            isExpanded ? "bx-chevron-down" : "bx-chevron-right"
                          }`}
                          style={{
                            fontSize: 18,
                            color: "#64748b",
                          }}
                        />
                      ) : (
                        <span style={{ width: 18 }} />
                      )}

                      <div className="flex-grow-1 text-truncate">
                        <div
                          className="fw-semibold"
                          style={{
                            fontSize: 14,
                            color: "#1e293b",
                          }}
                        >
                          {deviceModelType}
                        </div>

                        {checkedCount > 0 && (
                          <div
                            className="text-primary"
                            style={{ fontSize: 12 }}
                          >
                            {checkedCount} selected
                          </div>
                        )}
                      </div>

                      {firstDevice?.isOnline !== undefined &&
                        (firstDevice.isOnline ? (
                          <span className="font-size-11 badge-soft-success badge bg-secondary">
                            online
                          </span>
                        ) : (
                          <span className="font-size-11 badge-soft-danger badge bg-secondary">
                            offline
                          </span>
                        ))}
                    </button>

                    {/* AIR CURTAIN */}
                    {category === "aircurtain" && isExpanded && (
                      <div className="border-top">
                        {curtainDevices.map((device) => {
                          const itemId = device.id;
                          const selected = selectedSwitches[itemId];

                          const position = selected?.dimmerValue
                            ? parseInt(selected.dimmerValue, 16)
                            : 100;

                          return (
                            <div key={itemId} className="border-bottom py-2 px-3">
                              {/* CURTAIN NAME */}
                              <div className="d-flex justify-content-between align-items-center">
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`curtain-${itemId}`}
                                    checked={isChecked(itemId)}
                                    onChange={() => {
                                      toggleCurtainSelection(device);
                                    }}
                                  />

                                  <label
                                    className="form-check-label"
                                    htmlFor={`curtain-${itemId}`}
                                  >
                                    {device.name}
                                  </label>
                                </div>

                                {/* ON/OFF */}
                                {isChecked(itemId) && (
                                  <div className="form-check form-switch mb-0">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      role="switch"
                                      id={`curtain-onoff-${itemId}`}
                                      checked={selected?.isOn ?? false}
                                      onChange={() =>
                                        toggleIndividualOnOff(itemId)
                                      }
                                    />
                                  </div>
                                )}
                              </div>

                              {/* CURTAIN CONTROL */}
                           
                              {isChecked(itemId) && (
                                <DeviceControl
                                  category="aircurtain"
                                  itemId={itemId}
                                  selected={selectedSwitches[itemId]}
                                  onUpdate={updateSelectedItem}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* AIR DRIVER */}
                    {category === "airdriver" && isExpanded && (
                      <div className="border-top">
                        {allDrivers.map((driver) => {
                          const itemId = driver._id || driver.id!;
                          const selected = selectedSwitches[itemId];

                          return (
                            <div key={itemId} className="border-bottom py-2 px-3">
                              <div className="d-flex justify-content-between align-items-center">
                                {/* DRIVER CHECKBOX */}
                                <div className="form-check d-flex gap-10 ">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`driver-${itemId}`}
                                    checked={isChecked(itemId)}
                                    onChange={() => toggleDriverSelection(driver)}
                                  />

                                  <label
                                    className="form-check-label ps-2 "
                                    htmlFor={`driver-${itemId}`}
                                  >
                                    {driver.name || "Light"}
                                  </label>
                                  <div>
                                  <div
                                      className={` ms-2  ${
                                        driver.isOnline ? "badge-soft-success badge bg-secondary" : "badge-soft-danger badge bg-secondary"
                                      }`}
                                    >
                                       {driver.isOnline ? "online" : "offline"}
                                    </div>
                                  </div>
                                </div>
                              {/* ON OFF */}
                                {isChecked(itemId) && (
                                  <div className="form-check form-switch mb-0">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      role="switch"
                                      id={`driver-onoff-${itemId}`}
                                      checked={selected?.isOn ?? false}
                                      onChange={() =>
                                        toggleIndividualOnOff(itemId)
                                      }
                                    />
                                  </div>
                                )}
                              </div>

                              {/* BRIGHTNESS + TEMPERATURE */}
                              {isChecked(itemId) && (
                                  <DeviceControl
                                    category="airdriver"
                                    itemId={itemId}
                                    selected={selectedSwitches[itemId]}
                                    onUpdate={updateSelectedItem}
                                  />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* IR BLASTER */}
                    {category === "irblaster" && isExpanded && (
                      <div className="border-top">
                        {allBlasterRemotes.map((remote) => {
                          const itemId = remote.id;
                          const selected = selectedSwitches[itemId];

                          const temperature = selected?.temperature ?? 24;
                          const mode = selected?.blasterMode ?? "cool";
                          const fan = selected?.blasterFan ?? "1";
                          const swing = selected?.blasterSwing ?? "auto";

                          return (
                            <div key={itemId} className="border-bottom py-2 px-3">
                              {/* REMOTE NAME + ON/OFF */}
                              <div className="d-flex justify-content-between align-items-center">
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`blaster-${itemId}`}
                                    checked={isChecked(itemId)}
                                    onChange={() =>
                                      toggleBlasterSelection(remote)
                                    }
                                  />

                                  <label
                                    className="form-check-label"
                                    htmlFor={`blaster-${itemId}`}
                                  >
                                    {remote.displayName ||
                                      remote.name ||
                                      "AC Remote"}
                                  </label>
                                </div>

                                {isChecked(itemId) && (
                                  <div className="form-check form-switch mb-0">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      role="switch"
                                      id={`blaster-onoff-${itemId}`}
                                      checked={selected?.isOn ?? false}
                                      onChange={() =>
                                        toggleIndividualOnOff(itemId)
                                      }
                                    />
                                  </div>
                                )}
                              </div>

                              {/* BLASTER CONTROLS */}
                              {isChecked(itemId) && (
                                <div className="mt-3 ms-4">
                                  {/* TEMPERATURE */}
                                  <div className="d-flex justify-content-between">
                                    <span
                                      className="text-muted"
                                      style={{ fontSize: 12 }}
                                    >
                                      Temperature
                                    </span>

                                    <span
                                      className="fw-semibold"
                                      style={{ fontSize: 12 }}
                                    >
                                      {temperature}°C
                                    </span>
                                  </div>

                                  <input
                                    type="range"
                                    min="16"
                                    max="30"
                                    step="1"
                                    value={temperature}
                                    className="form-range mb-3"
                                    onChange={(e) => {
                                      const value = Number(e.target.value);

                                      setSelectedSwitches((prev) => ({
                                        ...prev,
                                        [itemId]: {
                                          ...prev[itemId],
                                          temperature: value,
                                        },
                                      }));
                                    }}
                                  />

                                  {/* MODE */}
                                  <div className="mb-3">
                                    <div
                                      className="text-muted mb-1"
                                      style={{ fontSize: 12 }}
                                    >
                                      Mode
                                    </div>

                                    <div className="d-flex flex-wrap gap-2">
                                      {["cool", "auto", "fan only", "heat"].map(
                                        (modeOption) => (
                                          <button
                                            key={modeOption}
                                            type="button"
                                            className={`btn btn-sm ${
                                              mode === modeOption
                                                ? "btn-primary"
                                                : "btn-light border"
                                            }`}
                                            onClick={() => {
                                              setSelectedSwitches((prev) => ({
                                                ...prev,
                                                [itemId]: {
                                                  ...prev[itemId],
                                                  blasterMode: modeOption as
                                                    | "cool"
                                                    | "auto"
                                                    | "fan only"
                                                    | "heat",
                                                },
                                              }));
                                            }}
                                          >
                                            {modeOption === "fan only"
                                              ? "Fan Only"
                                              : modeOption.charAt(0).toUpperCase() +
                                                modeOption.slice(1)}
                                          </button>
                                        ),
                                      )}
                                    </div>
                                  </div>

                                  {/* FAN SPEED */}
                                  <div className="mb-3">
                                    <div
                                      className="text-muted mb-1"
                                      style={{ fontSize: 12 }}
                                    >
                                      Fan Speed
                                    </div>

                                    <div className="d-flex flex-wrap gap-2">
                                      {["1", "2", "3", "4", "5"].map(
                                        (fanOption) => (
                                          <button
                                            key={fanOption}
                                            type="button"
                                            className={`btn btn-sm ${
                                              fan === fanOption
                                                ? "btn-primary"
                                                : "btn-light border"
                                            }`}
                                            onClick={() => {
                                              setSelectedSwitches((prev) => ({
                                                ...prev,
                                                [itemId]: {
                                                  ...prev[itemId],
                                                  blasterFan: fanOption as
                                                    | "1"
                                                    | "2"
                                                    | "3"
                                                    | "4"
                                                    | "5",
                                                },
                                              }));
                                            }}
                                          >
                                            {fanOption}
                                          </button>
                                        ),
                                      )}
                                    </div>
                                  </div>

                                  {/* SWING */}
                                  <div>
                                    <div
                                      className="text-muted mb-1"
                                      style={{ fontSize: 12 }}
                                    >
                                      Swing
                                    </div>

                                    <div className="d-flex flex-wrap gap-2">
                                      {[
                                        { value: "auto", label: "Auto" },
                                        { value: "h-swing", label: "H-Swing" },
                                        { value: "v-swing", label: "V-Swing" },
                                      ].map((swingOption) => (
                                        <button
                                          key={swingOption.value}
                                          type="button"
                                          className={`btn btn-sm ${
                                            swing === swingOption.value
                                              ? "btn-primary"
                                              : "btn-light border"
                                          }`}
                                          onClick={() => {
                                            setSelectedSwitches((prev) => ({
                                              ...prev,
                                              [itemId]: {
                                                ...prev[itemId],
                                                blasterSwing:
                                                  swingOption.value as
                                                    | "auto"
                                                    | "h-swing"
                                                    | "v-swing",
                                              },
                                            }));
                                          }}
                                        >
                                          {swingOption.label}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {/* NORMAL SWITCH */}
                    {category !== "aircurtain" &&
                      category !== "airdriver" &&
                      isExpanded && (
                        <div className="border-top">
                          {allSwitches.map((sw) => {
                            const switchType = sw.type ?? 0;

                            return (
                              <div
                                key={sw._id}
                                className="border-bottom py-2 px-3"
                              >
                                <div className="d-flex justify-content-between align-items-center">
                                  {/* SWITCH CHECKBOX */}
                                  <div className="form-check">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id={`sw-${sw._id}`}
                                      checked={isChecked(sw._id)}
                                      onChange={() => toggleSwitchSelection(sw)}
                                    />

                                    <label
                                      className="form-check-label"
                                      htmlFor={`sw-${sw._id}`}
                                    >
                                      {sw.displayName ||
                                        `Switch ${sw.button ?? ""}`.trim()}
                                    </label>
                                  </div>

                                  {/* ON OFF */}
                                {isChecked(sw._id) &&
                                <div className="form-check form-switch mb-0">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      role="switch"
                                      id={`sw-onoff-${sw._id}`}
                                      checked={
                                        selectedSwitches[sw._id]?.isOn ?? false
                                      }
                                      onChange={() =>
                                        toggleIndividualOnOff(sw._id)
                                      }
                                    />
                                  </div>}
                                </div>
                                {isChecked(sw._id) && (
                                  <DeviceControl
                                    category="switch"
                                    itemId={sw._id}
                                    selected={selectedSwitches[sw._id]}
                                    switchType={switchType}
                                    onUpdate={updateSelectedItem}
                                  />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>
    );
  };
  return (
    <Offcanvas isOpen={isOpen} toggle={toggle} direction="end">
      <OffcanvasHeader
        toggle={toggle}
        className="d-flex align-items-center justify-content-between w-100"
      >
        {editingScene ? "Edit Scene" : "Create Scene"}
      </OffcanvasHeader>
      <OffcanvasBody className="d-flex flex-column">
        {step === 1 && (
          <>
            <div className="mb-3">
              <Label htmlFor="scene-name">Scene Name</Label>
              <Input
                id="scene-name"
                type="text"
                placeholder="Enter scene name"
                value={sceneName}
                 onChange={(e) => {
                  const value = e.target.value;
                  if (value && !/^[a-zA-Z]/.test(value)) return;
                  setSceneName(value);
                }}
                autoFocus
              />
            </div>

            <div className="mt-auto d-flex justify-content-end gap-2">
              <Button color="light" onClick={toggle}>
                Cancel
              </Button>
              <Button color="primary" onClick={goToNextFromName}>
                Next
              </Button>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <div className="mb-2">
              <span className="text-muted">Scene: </span>
              <strong>{sceneName}</strong>
            </div>

            <div className="d-flex justify-content-between align-items-center mb-3">
              <Label className="mb-0 fw-bold">
                Select Devices
              </Label>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="all-on-off"
                  checked={allOn}
                  onChange={handleAllOnOffToggle}
                />
                <label className="form-check-label" htmlFor="all-on-off">
                  All {allOn ? "On" : "Off"}
                </label>
              </div>
            </div>

            {loadingDevices ? (
              <div className="text-center py-4">
                <Spinner size="sm" /> Loading devices...
              </div>
            ) : (
              <div
                className="flex-grow-1 overflow-auto"
                style={{ maxHeight: "100vh" }}
              >
                    {(Object.keys(CATEGORY_LABELS) as CategoryKey[]).map((cat) => (
                      <div key={cat}>
                        <div className="fw-bold text-muted small text-uppercase mt-2 mb-1">
                          {CATEGORY_LABELS[cat]}
                        </div>

                        {renderDeviceList(
                          cat,
                          devicesByCategory[cat] || [],
                        )}
                      </div>
                    ))}
              </div>
            )}

            <div className="mt-3 d-flex justify-content-end gap-2">
              <Button
                color="light"
                onClick={() => {
                  setAllOn(true);
                  setStep(1);
                }}
              >
                Back
              </Button>
              <Button
                color="primary"
                onClick={handleCreateScene}
                disabled={submitting}
              >
                {submitting ? (
                  <Spinner size="sm" />
                ) : editingScene ? (
                  "Update"
                ) : (
                  "Create"
                )}
              </Button>
            </div>
          </>
        )}
      </OffcanvasBody>
    </Offcanvas>
  );
};

export default CreateSceneOffcanvas;